import {CountryCode} from 'libphonenumber-js';
import {formatPhoneNumber} from 'platform/locale';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetCurrentUserInfoQuery, useGetCustomerV2Query} from '@dms/api/customer';
import {
  InsurancePerson,
  InsurancePersonV2,
  InsuranceVehicle,
  InsuranceVehicleV2,
} from '@dms/api/finance';
import {
  InsuranceSpecificationsResponseBody,
  useGetBusinessCaseQuery,
  useGetInsuranceSpecificationsQuery,
  useGetVehicleQuery,
} from '@dms/api/sales';
import {GetCurrentUserInfoApiResponse, TenantResponseBody} from '@dms/api/shared';
import {getNaturalPersonFullName} from '@dms/shared';

import {Nullish} from 'shared/web';

export type InsuranceData = {
  role: string;
  insurerNumber: string | undefined;
  insuranceVehicle: InsuranceVehicle | InsuranceVehicleV2 | null;
  insurancePerson: InsurancePerson | InsurancePersonV2 | null;
  insuranceOffer: InsuranceSpecificationsResponseBody['insurance'] | null;
  vehicleTitle: string | null;
  vehicleId: string | null;
  personName: string | null;
  tenantInfo: TenantResponseBody | undefined;
  userInfo: GetCurrentUserInfoApiResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  isFinished: boolean;
};

// eslint-disable-next-line complexity -- Pre-existing violation
export function useInsuranceData(
  businessCaseId: string,
  skipForNonInsurer?: boolean
): InsuranceData {
  const {data: userInfo} = useGetCurrentUserInfoQuery();
  const {data: tenantInfo} = useGetTenantQuery();

  const insurerNumber = userInfo?.settings.insurerNumber;
  const skip = skipForNonInsurer && isNilOrEmpty(insurerNumber);

  const {
    data: businessCase,
    isLoading: isBusinessCaseLoading,
    isError: isBusinessCaseError,
  } = useGetBusinessCaseQuery({businessCaseId}, {skip, refetchOnMountOrArgChange: true});

  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useGetCustomerV2Query(
    {customerId: businessCase?.customerId ?? ''},
    {skip: isNilOrEmpty(businessCase?.customerId)}
  );

  const offerVehicle = businessCase?.offers?.[0]?.saleVehicles?.[0];

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId: offerVehicle?.vehicleId ?? ''},
    {skip: skip || !offerVehicle?.vehicleId, refetchOnMountOrArgChange: true}
  );

  const insuranceVehicle: InsuranceVehicle = {
    sellingPrice: vehicle?.sellingPrice?.withVat?.amount ?? undefined,
    firstRegistrationOnDay: vehicle?.firstRegistrationOnDay ?? undefined,
    firstRegistrationOnMonth: vehicle?.firstRegistrationOnMonth ?? undefined,
    firstRegistrationOnYear: vehicle?.firstRegistrationOnYear ?? undefined,
    engineVolume: vehicle?.engine?.engineVolume,
    power: vehicle?.power,
    mileage: vehicle?.vehicle?.mileage,
    weight: vehicle?.vehicle?.weight,
    modelFamily: vehicle?.modelFamily,
    fuelType: vehicle?.fuelType,
    make: vehicle?.make,
    type: vehicle?.type,
    vin: vehicle?.vin ?? undefined,
    registrationPlate: vehicle?.vehicle?.registrationPlate,
    vtpNumber: vehicle?.vehicle?.vehicleRegistrationNumber,
    seatCount: vehicle?.vehicle?.seatCount,
  };

  const {
    data: insuranceOffer,
    isLoading: isInsuranceOfferLoading,
    isError: isInsuranceOfferError,
  } = useGetInsuranceSpecificationsQuery(
    {businessCaseId},
    {skip: !!skip || isNilOrEmpty(offerVehicle) || isNilOrEmpty(vehicle)}
  );
  const contactPerson =
    customer?.contacts.find((person) => businessCase?.contactPersonId === person.id) ||
    customer?.foundingPerson;

  const personName = getNaturalPersonFullName(contactPerson);
  const personEmail = contactPerson?.emails?.[0]?.email;
  const phoneNumberData = contactPerson?.phoneNumbers?.[0];
  const personPhoneNumber =
    phoneNumberData &&
    formatPhoneNumber(
      `${phoneNumberData?.prefix}${phoneNumberData?.number}`,
      phoneNumberData?.countryCode as CountryCode | Nullish
    );

  const insurancePerson = {
    firstName: contactPerson?.firstName,
    lastName: contactPerson?.lastName,
    titleBefore: contactPerson?.titleBefore,
    email: personEmail,
    phoneNumber: personPhoneNumber,
    personalIdentifier: contactPerson?.personalIdentifier,
    street: contactPerson?.permanentAddress?.address.street,
    descriptiveNumber: contactPerson?.permanentAddress?.address.descriptiveNumber,
    OrientationNumber: contactPerson?.permanentAddress?.address.orientationNumber,
    city: contactPerson?.permanentAddress?.address.city,
    zip: contactPerson?.permanentAddress?.address.zip,
    companyName: customer?.businessInfo?.businessInfo.tradeName,
    registrationNumber: customer?.selfEmployed
      ? customer?.selfEmployedBusinessInfoData?.registrationNumber
      : customer?.businessInfo?.businessInfo.registrationNumber,
    selfEmployed: customer?.selfEmployed,
    customerDiscriminator: customer?.discriminator,
  };

  const role = insuranceOffer?.userRole ?? 'NONE';

  return {
    role,
    insurerNumber,
    insuranceVehicle,
    insurancePerson,
    personName,
    insuranceOffer: insuranceOffer?.insurance ?? null,
    vehicleTitle: vehicle?.title ?? null,
    vehicleId: vehicle?.id ?? null,
    tenantInfo,
    userInfo,
    isLoading:
      isCustomerLoading || isBusinessCaseLoading || isVehicleLoading || isInsuranceOfferLoading,
    isError: isCustomerError || isBusinessCaseError || isVehicleError || isInsuranceOfferError,
    isFinished: Boolean(businessCase?.customerRecommended || businessCase?.insuranceOffered),
  };
}
